/**
 * Auth endpoints.
 *
 * The patient app supports TWO login methods (per
 * patient_frontend_plan.md §4.P1):
 *   1. Mobile + OTP — sendOtp() then verifyOtp()
 *   2. Email + password — loginWithPassword()
 *
 * Both return an AuthTokenResponse the caller passes to TokenStore.setTokens.
 */

import type {
  AuthTokenResponse,
  LoginRequest,
  LoginResult,
  MobileLoginRequest,
  RegisterRequest,
  RegisterResponse,
  SendOtpRequest,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '@cardioq/engine-types';
import { getActiveTokenStore, getApiClient } from './axios';
import { toApiError } from './errors';

/** `dev_otp` is only ever present in local dev with OTP_DEBUG on (no mail
 *  sender configured) — the backend never returns it outside APP_ENV=dev. */
export async function sendOtp(
  body: SendOtpRequest,
): Promise<{ message: string; dev_otp?: string }> {
  try {
    const r = await getApiClient().post('/auth/send-otp', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * NOTE: backend's `/auth/verify-otp` returns a verification TOKEN, not
 * an access token. That token then feeds into /auth/register for new
 * users. The patient app currently uses email+password login for
 * returning users — OTP-only login (no password) is not yet a backend
 * flow. To switch to OTP-only login, the backend would need a new
 * "/auth/login-via-otp" endpoint.
 */
export async function verifyOtp(body: VerifyOtpRequest): Promise<VerifyOtpResponse> {
  try {
    const r = await getApiClient().post<VerifyOtpResponse>('/auth/verify-otp', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Create the account after OTP verification. Backend creates the User
 * AND (for PATIENT role with a mobile number) the linked Patient record
 * in one transaction. Does NOT return tokens — call loginWithPassword
 * right after to establish the session.
 */
export async function registerAccount(
  body: RegisterRequest,
): Promise<RegisterResponse> {
  try {
    const r = await getApiClient().post<RegisterResponse>('/auth/register', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function loginWithPassword(
  body: LoginRequest,
): Promise<LoginResult> {
  try {
    const r = await getApiClient().post<LoginResult>('/auth/login', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Complete a 2FA-gated login (governance roles: ADMIN_FULL / APPROVER /
 * ADMIN) by exchanging the challenge token + the emailed 6-digit OTP
 * for a full token response.
 */
export async function loginVerify2fa(
  twofaToken: string,
  otp: string,
): Promise<AuthTokenResponse> {
  try {
    const r = await getApiClient().post<AuthTokenResponse>('/auth/login-2fa', {
      twofa_token: twofaToken,
      otp,
    });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Rotate the signed-in user's password. Provisioned accounts carry
 * `must_change_password` on their login response and must call this
 * (with the temporary password as `currentPassword`) before proceeding.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  try {
    await getApiClient().post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Mobile-number + password login.
 *
 * ⚠️ BACKEND NOT YET IMPLEMENTED — posts to the same `/auth/login`
 * endpoint with a `mobile_number` field. The current backend
 * LoginRequest accepts `email` only, so this returns a 422 until login
 * is extended to resolve a user by mobile number. The UI surfaces a
 * clear message on failure. See ui_pending_backend.md → "Mobile login".
 */
export async function loginWithMobile(
  body: MobileLoginRequest,
): Promise<LoginResult> {
  try {
    const r = await getApiClient().post<LoginResult>('/auth/login', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function forgotPassword(email: string): Promise<{ sent: boolean }> {
  try {
    const r = await getApiClient().post('/auth/forgot-password', { email });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  try {
    await getApiClient().post('/auth/reset-password', { token, new_password: newPassword });
  } catch (e) {
    throw toApiError(e);
  }
}

export async function logout(): Promise<void> {
  // Clear local tokens regardless of server response (logout is a
  // local-state operation as well as a server-revocation one).
  try {
    const refresh = getActiveTokenStore().getRefreshToken();
    await getApiClient().post('/auth/logout', refresh ? { refresh_token: refresh } : {});
  } catch {
    // Swallow — server may have already invalidated the token.
  } finally {
    getActiveTokenStore().clear();
  }
}

// ===========================================================================
// Patient mobile sign-in (SMS code) — one flow for sign-up and log-in
// ===========================================================================

export interface MobileSendOtpResult {
  message: string;
  expires_in_seconds: number;
  /** Local dev only (APP_ENV=dev + OTP_DEBUG, no SMS provider). */
  dev_otp?: string | null;
}

export interface MobileAuthResult {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in_seconds: number;
  user_id: string;
  /** Null for a patient who signed up with mobile only. */
  email: string | null;
  name: string;
  role: string;
  must_change_password: boolean;
  is_new_user: boolean;
}

/** Text a 6-digit sign-in code. `mobile` is 10 digits (+91 is implied). */
export async function sendMobileOtp(mobile: string): Promise<MobileSendOtpResult> {
  try {
    const r = await getApiClient().post<MobileSendOtpResult>('/auth/mobile/send-otp', { mobile });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Verify the code and sign in. A number with no account yet is signed UP —
 * `name` is required for that case and ignored for a returning patient.
 */
export async function verifyMobileOtp(body: {
  mobile: string;
  otp: string;
  name?: string;
}): Promise<MobileAuthResult> {
  try {
    const r = await getApiClient().post<MobileAuthResult>('/auth/mobile/verify', body);
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}
